import { useLocale } from '@mirai/locale';
import { Notification, Progress, styles, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { CONTAINER_ID, SCRIPT_ID, SCRIPT_URL } from './FormMercadopago.constants';
import { L10N } from './FormMercadopago.l10n';
import * as style from './FormMercadopago.module.css';
import { loadScript } from '../../../../../helpers/loadScript';
import { trackError } from '../../../../../helpers/trackError';

export const FormMercadopago = ({ response, ...others }) => {
  const { language, translate } = useLocale();

  const [error, setError] = useState();
  const [paymentReady, setPaymentReady] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!response) return;

    let mounted = true;
    let cleanup;

    (async () => {
      try {
        cleanup = await loadScript({
          domain: 'payments',
          id: SCRIPT_ID,
          src: SCRIPT_URL,
          onLoad: () => setReady(true),
        });
        if (mounted && window.paymentBrickController) setReady(true);
      } catch (nextError) {
        setError(nextError);
      }
    })();

    return () => {
      mounted = false;
      cleanup && cleanup();
      window?.paymentBrickController?.unmount();
    };
  }, [response]);

  useEffect(() => {
    if (!ready || !response) return;

    const { configuration: { amount, preferenceId, publicKey } = {} } = response;
    const { MercadoPago } = window;

    try {
      setError();
      setPaymentReady(false);
      const mercadoPago = new MercadoPago(publicKey);
      const bricksBuilder = mercadoPago.bricks();

      const renderPaymentBrick = async (bricksBuilder) => {
        const settings = {
          initialization: { amount, preferenceId },
          locale: language,
          customization: {
            paymentMethods: {
              atm: 'all',
              ticket: 'all',
              creditCard: 'all',
              prepaidCard: 'all',
              debitCard: 'all',
              mercadoPago: 'all',
            },
          },
          callbacks: {
            onReady: () => {
              setPaymentReady(true);
            },
            onSubmit: ({
              formData: {
                transaction_amount,
                token,
                installments,
                payment_method_id,
                issuer_id,
                payer: { identification: { number, type } = {} } = {},
              } = {},
            } = {}) => {
              const { configuration: { paymentTransactionId, returnUrl } = {} } = response;
              const url = new URL(returnUrl);
              paymentTransactionId && url.searchParams.append('paymentTransactionId', paymentTransactionId);
              transaction_amount && url.searchParams.append('transaction_amount', transaction_amount);
              token && url.searchParams.append('token', token);
              installments && url.searchParams.append('installments', installments);
              payment_method_id && url.searchParams.append('payment_method_id', payment_method_id);
              issuer_id && url.searchParams.append('issuer_id', issuer_id);
              number && url.searchParams.append('payer_id_number', number);
              type && url.searchParams.append('payer_id_type', type);

              window.location.href = url.toString();
            },
            onError: (result) => {
              if (result) trackError('Mercadopago', result instanceof Error ? result.stack : result?.error);
              setError(result);
            },
          },
        };
        window.paymentBrickController = await bricksBuilder.create('payment', CONTAINER_ID, settings);
      };
      renderPaymentBrick(bricksBuilder);
    } catch (nextError) {
      setError(nextError);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  return (
    <View {...others} className={styles(others.className, style.container)}>
      <Progress indeterminate visible={!paymentReady && !error} />
      <View id={CONTAINER_ID} />
      {error && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR)}
        </Notification>
      )}
    </View>
  );
};

FormMercadopago.displayName = 'Mirai:Payments:ButtonPayment:FormMercadopago';

FormMercadopago.propTypes = {
  response: PropTypes.shape(),
};
